import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';

import { AuthService } from '../../../services/auth.service';
import type { TopicPeriod, YearGroup } from '../../../models';

const MIN_YEAR = 2025;
const MIN_MONTH = 1;

@Component({
  selector: 'app-topic-selector',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './topic-selector.component.html',
  styleUrl: './topic-selector.component.scss',
  imports: [RouterLink]
})
export class TopicSelectorComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);

  protected readonly yearGroups = signal<YearGroup[]>(this.buildYearGroups());
  protected readonly mode = signal<'view' | 'practice'>('view');

  ngOnInit(): void {
    const modeParam = this.route.snapshot.queryParamMap.get('mode');
    if (modeParam === 'practice') {
      this.mode.set('practice');
    } else {
      this.mode.set('view');
    }
  }

  protected selectPeriod(period: TopicPeriod): void {
    const mode = this.mode();
    if (mode === 'practice' && !this.auth.isAuthenticated()) {
      this.auth.requestLogin();
      return;
    }
    const basePath = mode === 'practice' ? 'practice' : 'topics';
    this.router.navigate(['/writing', basePath, period.year, period.month]);
  }

  protected setMode(mode: 'view' | 'practice'): void {
    if (mode === 'practice' && !this.auth.isAuthenticated()) {
      this.auth.requestLogin();
      return;
    }
    this.mode.set(mode);
  }

  private buildYearGroups(): YearGroup[] {
    const now = new Date();
    const periods: TopicPeriod[] = [];
    let y = now.getFullYear();
    let m = now.getMonth() + 1;

    while (this.isWithinAllowedWindow(y, m)) {
      periods.push({
        year: y,
        month: m,
        label: `${y}年${m}月`,
        combinationId: `comb-${y}-${m}`
      });
      m--;
      if (m < 1) {
        m = 12;
        y--;
      }
    }

    const byYear = new Map<number, TopicPeriod[]>();
    for (const p of periods) {
      const list = byYear.get(p.year) ?? [];
      list.push(p);
      byYear.set(p.year, list);
    }

    return Array.from(byYear.entries())
      .sort(([a], [b]) => b - a)
      .map(([year, months]) => ({ year, months }));
  }

  private isWithinAllowedWindow(year: number, month: number): boolean {
    if (!Number.isFinite(year) || !Number.isFinite(month)) return false;
    if (month < 1 || month > 12) return false;

    const target = year * 100 + month;
    const min = MIN_YEAR * 100 + MIN_MONTH;

    const now = new Date();
    const current = now.getFullYear() * 100 + (now.getMonth() + 1);

    return target >= min && target <= current;
  }
}
